import {
    AbstractControl, FormControl, FormGroup, FormRecord
} from "@angular/forms";
import {
    EnumUtil, IGenericLocalization, ILocalizableModel, Language, LocalizationUtil
} from "@shopliftr/common-js/data";

import { IImageControlValue } from "../shared/image/interface/image-control-value";

/**
 * Builds the per-language `FormRecord` a Localized Domain Form's `FormGroup`
 * extends via {@link ILocalizedFormGroup}.
 *
 * Field discovery comes from the model's own `localizedFields` metadata —
 * never a hard-coded list — via `LocalizationUtil.localizationTextFields` /
 * `localizationImageFields`. Text fields are filtered to ones whose value can
 * actually round-trip through `getValue`/`setValue` on a localization
 * instance; a field that throws on read is skipped rather than crashing
 * construction, so a model whose metadata includes a non-string localized
 * field (`localizationTextFields()` can return `Enum` fields even though
 * `AbstractLocalization.getValue`/`setValue` only support `String`-validator
 * ones) degrades to "not editable here" instead of breaking the form.
 */
export class LocalizationFormUtil {

    /**
     * Builds one `FormGroup` per non-English language, keyed by {@link Language},
     * with control keys mirroring the model's own localized field names.
     *
     * @param model - The model whose `localizedFields` metadata drives field
     * discovery, and whose existing localizations (if any) seed initial values
     * @typeParam C - The per-language control shape, e.g. `IChainLocalizationFormGroup`.
     * Defaults to the untyped `Record<string, AbstractControl>` so every existing call site
     * compiles unchanged; a caller that already knows its shape can name it and get a typed
     * `FormRecord` back with no cast of its own. Constrained the same way {@link ILocalizedFormGroup}
     * constrains its own `L` - a mapped `{[K in keyof C]: AbstractControl}`, not `Record<string,
     * AbstractControl>` - because a plain `Record` constraint requires an actual index signature
     * on whatever's passed for `C`, which a specific named interface like
     * `IChainLocalizationFormGroup` doesn't have even though every one of its properties is
     * assignable to `AbstractControl`.
     * @returns A `FormRecord` ready to assign to a Localized Domain Form's
     * `localizations` control
     */
    static createLocalizationsRecord<
        M extends ILocalizableModel,
        C extends { [K in keyof C]: AbstractControl } = Record<string, AbstractControl>
    >(
        model: M
    ): FormRecord<FormGroup<C>> {

        const textFields = LocalizationUtil.localizationTextFields(model);
        const imageFields = LocalizationUtil.localizationImageFields(model);
        const languages = EnumUtil.getValues(Language, [Language.English]);

        const groups: Record<string, FormGroup<C>> = {};

        for (const language of languages) {

            // `model` is only known here by its bare `ILocalizableModel` constraint (L defaults
            // to `unknown`), so `getLocalization` returns `unknown` - cast to the shape both
            // branches actually share (`IGenericLocalization`) rather than threading a second
            // generic parameter through that TS cannot infer from `model`'s constraint alone.
            // This is the *localization instance* type - a different generic from `C` below,
            // which the caller already knows and supplies explicitly.
            const localization = (model.getLocalization(language) as IGenericLocalization | undefined)
                ?? LocalizationUtil.createLocalization(model, language);

            const controls: Record<string, AbstractControl> = {};

            for (const field of textFields) {

                let value = "";
                try {
                    value = localization.getValue(field) ?? "";
                }
                catch {
                    // Not a String-validator field on this localization type —
                    // localizationTextFields() over-reports (it can return
                    // Enum fields too); skip it rather than crash construction.
                    continue;
                }
                controls[field] = new FormControl<string>(value);
            }

            for (const field of imageFields) {

                const persistedUrl = (localization as unknown as Record<string, unknown>)[field] as string | undefined;
                const value: IImageControlValue | undefined = persistedUrl ? { imageUrl: persistedUrl } : undefined;
                controls[field] = new FormControl<IImageControlValue | undefined>(value);
            }

            // `controls`' actual keys are only known at runtime, driven by the model's own
            // metadata - nothing statically ties them to `C`. This single cast is the
            // contained cost of that runtime discovery, made once here where the reason for it
            // is documented, rather than as an unexplained `as unknown as` at every call site.
            groups[language] = new FormGroup(controls) as unknown as FormGroup<C>;
        }

        return new FormRecord(groups);
    }
}
